import { useContext, useMemo } from 'react';

import type { StorefrontLanguage } from '@/features/storefront/api/storefront';
import { useStorefrontLanguage } from '@/features/storefront/i18n/context';
import type { LanguageDisplay } from '@/features/storefront/sections/schema';
import { StorefrontThemeContext } from '@/features/storefront/storefrontThemeContext';
import {
  themeLanguageSwitcherSchema,
  type LanguageSwitcherPlacement,
  type ThemeLanguageSwitcher,
} from '@/features/storefront/themeSchema';

/**
 * What a language switcher needs to know before it draws: how a language is
 * named, and whether the theme and the store want a switcher at a placement.
 * Kept apart from the components in `LanguageSwitcher.tsx`.
 */

/** How one language reads in a switcher, per `display`. */
export function languageLabel(language: StorefrontLanguage, display: LanguageDisplay): string {
  const code = language.code.toUpperCase();
  switch (display) {
    case 'name':
      return language.name;
    case 'code':
      return code;
    case 'native_code':
      return `${language.native_name} (${code})`;
    case 'native':
    default:
      return language.native_name;
  }
}

/** The theme's switcher settings, defaults filled in. */
export function useThemeLanguageSwitcher(): ThemeLanguageSwitcher {
  const theme = useContext(StorefrontThemeContext);
  const raw = theme?.language_switcher;
  return useMemo(() => themeLanguageSwitcherSchema.parse(raw ?? {}), [raw]);
}

/**
 * Whether the theme's switcher is drawn at `placement`: the store allows
 * switching, the theme shows it there, and there is something to switch to.
 */
export function useLanguageSwitcherVisible(placement: LanguageSwitcherPlacement): boolean {
  const settings = useThemeLanguageSwitcher();
  const { switcherEnabled, languages } = useStorefrontLanguage();
  return (
    switcherEnabled &&
    settings.show &&
    settings.placements.includes(placement) &&
    languages.length > 1
  );
}
